import type { Point, Polygon } from "geojson";
import type { FincaConfiguracionItem } from "../../../generated/grpc/configuracion_finca_pb";
import type { Finca, Lote } from "../../../generated/grpc/login_pb";
import { fx_crearBaseLocal, reqToPromise, txDone } from "../../../utils/idbUtils";
import { wkbToPoint, wkbToPolygon } from "../../../utils/wkbUtils";
import { ClienteFincaBaseResponse } from "../response/ClienteFincaBaseResponse";

const DB_NAME = "grpc_login_fincas";
const DB_VERSION = 3;
const STORE_FINCAS = "fincas";
const STORE_LOTES = "lotes";

/** Finca tal como queda guardada: el login la trae con las geometrías en WKB
 *  (ver CLAUDE.md, sección migración a gRPC-Web — se prioriza el tamaño de la
 *  respuesta), acá ya decodificadas a GeoJSON (mismo shape que usa REST en
 *  `ClienteFincaBaseResponse`) y sin `lotes` (van en su propio store). */
export type StoredFinca = Omit<
  Finca,
  "lotes" | "ubicacion" | "sateliteAreaGeografica"
> & {
  ubicacion: Point | null;
  sateliteAreaGeografica: Polygon | null;
};

/** Campos de `StoredFinca` sin el `$typeName` de protobuf-es: lo que hace
 *  falta para guardar una finca puntual, sin atarse a que haya salido
 *  literalmente de un mensaje `Finca` de gRPC (`upsertFincaAsync` la arma
 *  ella misma) — así una finca creada por el wizard (`GuardarResponse`, mismo
 *  set de campos salvo `lotes`) o guardada por un form de configuración (REST,
 *  `ClienteFincaBaseResponse`) puede convertirse a este tipo sin pelearse con
 *  el `$typeName` de un mensaje que no es. */
export type FincaParaGuardar = Omit<StoredFinca, "$typeName" | "$unknown">;

/** Lote tal como queda guardado: `areaGeografica` ya decodificada a GeoJSON. */
export type StoredLote = Omit<Lote, "areaGeografica"> & {
  areaGeografica: Polygon;
};

/** Campos de `StoredLote` sin el `$typeName` de protobuf-es — mismo criterio
 *  que `FincaParaGuardar`: los lotes que devuelve el wizard al finalizar
 *  (`GuardarResponse.lotes`) traen el mismo set de campos que `Lote` (login)
 *  pero no son ese mensaje, y no tienen por qué pelearse con su `$typeName`
 *  (lo pone `replaceLotesDeFincaAsync`). */
export type LoteParaGuardar = Omit<StoredLote, "$typeName" | "$unknown">;

/** IndexedDB dedicada para las fincas + lotes que trae el login por gRPC-Web
 *  (ver `CLAUDE.md` del Server, sección migración a gRPC-Web). A propósito,
 *  base propia y no un store compartido: cada isla gRPC tiene su propia
 *  conexión IndexedDB (nombre y versión propios), así el upgrade de una no
 *  arriesga el resto — mismo criterio que `GrpcCatalogosDatabase`.
 *
 *  Sin delta: el login siempre manda la lista completa, así que
 *  `replaceAllAsync` simplemente reemplaza todo el contenido en cada
 *  login/checkToken.
 *
 *  `getFincasAsync()` es la fuente real de `ClienteAsideMenuRender`/
 *  `RegistroFincaPaso3Render` (ver `toClienteFincaBaseResponse` más abajo).
 *  `getLotesByFincaIdAsync` la usa `ClienteAsideMenuRender._esCompletaAsync`
 *  (una finca sin lotes no es "completa", aunque tenga AOI). */
class GrpcFincaLoteDatabase {
  /** Conexión a la base local con el ayudante común, que al cambiar de
   *  versión conserva los datos (ver `fx_crearBaseLocal`). La versión 3
   *  (2026-09-30) se subió a pedido del usuario para una limpieza única al
   *  estrenar el ayudante: un navegador con una versión anterior vacía la
   *  base una sola vez y la vuelve a llenar desde el servidor. */
  private readonly _base = fx_crearBaseLocal(
    DB_NAME,
    DB_VERSION,
    [
      { nombre: STORE_FINCAS, clave: "id" },
      { nombre: STORE_LOTES, clave: "id", indices: ["fincaId"] },
    ],
    { vaciarAntesDe: 3 },
  );

  /** Conexión a la base local, abierta la primera vez y reusada después. */
  private open(): Promise<IDBDatabase> {
    return this._base.abrir();
  }

  /** Reemplaza todo el contenido con lo que trajo el login: borra ambos
   *  stores y vuelve a escribir en una única transacción. Los lotes vienen
   *  anidados en cada finca (`finca.lotes`); acá se separan en su propio
   *  store. Las geometrías llegan en WKB (bytes) y se decodifican a GeoJSON
   *  acá, una sola vez, para que lo que quede guardado (y lo que devuelven
   *  `getFincasAsync`/`getLotesByFincaIdAsync`) tenga un shape estable — nada
   *  más en la app necesita enterarse del transporte. */
  async replaceAllAsync(fincas: readonly Finca[]): Promise<void> {
    const db = await this.open();

    return new Promise((resolve, reject) => {
      const tx = db.transaction([STORE_FINCAS, STORE_LOTES], "readwrite");
      const fincaStore = tx.objectStore(STORE_FINCAS);
      const loteStore = tx.objectStore(STORE_LOTES);

      fincaStore.clear();
      loteStore.clear();

      for (const finca of fincas) {
        const { lotes, ubicacion, sateliteAreaGeografica, ...fincaSinLotes } =
          finca;
        const storedFinca: StoredFinca = {
          ...fincaSinLotes,
          ubicacion: ubicacion ? wkbToPoint(ubicacion) : null,
          sateliteAreaGeografica: sateliteAreaGeografica
            ? wkbToPolygon(sateliteAreaGeografica)
            : null,
        };
        fincaStore.put(storedFinca);

        for (const lote of lotes) {
          const storedLote: StoredLote = {
            ...lote,
            areaGeografica: wkbToPolygon(lote.areaGeografica),
          };
          loteStore.put(storedLote);
        }
      }

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  /** Actualiza (o inserta) una sola finca sin tocar el resto de lo cacheado —
   *  a diferencia de `replaceAllAsync` (pensado para el login, que sí manda
   *  el listado completo), esto es lo que llaman los guardados puntuales
   *  (wizard, configuración de ganadería/árboles) para que el sidebar vea el
   *  cambio sin pedirle de nuevo el listado entero al server.
   *
   *  **No toca el store de lotes**, a propósito: los guardados de
   *  configuración (ganadería/árboles) no cambian los lotes. Quien sí los
   *  cambia —el wizard, que crea la finca entera— llama además a
   *  `replaceLotesDeFincaAsync`; sin eso la finca queda cacheada sin lotes y
   *  `_esCompletaAsync` la da por incompleta (ver esa función). */
  async upsertFincaAsync(finca: FincaParaGuardar): Promise<void> {
    const db = await this.open();
    const tx = db.transaction(STORE_FINCAS, "readwrite");
    tx.objectStore(STORE_FINCAS).put({
      ...finca,
      $typeName: "sextante.seguridad.login.v1.Finca",
    } satisfies StoredFinca);
    await txDone(tx);
  }

  /** Reemplaza los lotes cacheados de UNA finca (borra los que tuviera y
   *  escribe los que llegan), sin tocar los de las demás — la contraparte por
   *  finca de `replaceAllAsync`. Lo llama el wizard al finalizar, con los
   *  lotes que devuelve el server (`GuardarResponse.lotes`): el store de
   *  lotes lo llenaba solo el login, así que hasta el siguiente login una
   *  finca recién creada quedaba sin lotes acá y `_esCompletaAsync`
   *  (`ClienteAsideMenuRender`) la clasificaba como incompleta — el
   *  recordatorio "Terminar registro de …" aparecía sobre una finca que ya
   *  estaba entera.
   *
   *  Borrado + escritura en UNA transacción: si falla algo, no queda la finca
   *  con los lotes viejos borrados y los nuevos sin escribir. */
  async replaceLotesDeFincaAsync(
    fincaId: string,
    lotes: readonly LoteParaGuardar[],
  ): Promise<void> {
    const db = await this.open();
    const tx = db.transaction(STORE_LOTES, "readwrite");
    const store = tx.objectStore(STORE_LOTES);

    const idsPrevios = await reqToPromise<IDBValidKey[]>(
      store.index("fincaId").getAllKeys(fincaId),
    );
    for (const id of idsPrevios) store.delete(id);

    for (const lote of lotes) {
      store.put({
        ...lote,
        $typeName: "sextante.seguridad.login.v1.Lote",
      } satisfies StoredLote);
    }

    await txDone(tx);
  }

  /** Todas las fincas guardadas por el último login/checkToken, sin sus
   *  lotes (viven en su propio store — ver `getLotesByFincaIdAsync`). Esta
   *  base solo guarda las del cliente logueado, así que no hace falta
   *  filtrar por clienteId. */
  async getFincasAsync(): Promise<StoredFinca[]> {
    const db = await this.open();
    const store = db
      .transaction(STORE_FINCAS, "readonly")
      .objectStore(STORE_FINCAS);
    return reqToPromise<StoredFinca[]>(store.getAll());
  }

  /** Lotes de una finca puntual, vía el índice `fincaId`. */
  async getLotesByFincaIdAsync(fincaId: string): Promise<StoredLote[]> {
    const db = await this.open();
    const index = db
      .transaction(STORE_LOTES, "readonly")
      .objectStore(STORE_LOTES)
      .index("fincaId");
    return reqToPromise<StoredLote[]>(index.getAll(fincaId));
  }
}

/** Singleton de la base de fincas/lotes traídos por login vía gRPC-Web. */
export const grpcFincaLoteDatabase = new GrpcFincaLoteDatabase();

/** Adapta una `StoredFinca` (gRPC) al shape `ClienteFincaBaseResponse` (REST)
 *  que ya consume el resto de la app — en particular `IFincaSelectedService`,
 *  la finca "actualmente seleccionada" que leen ~50 archivos vía
 *  `serviceProvider.getFincaSelectedService()`, tipada a `ClienteFincaBaseResponse`
 *  a propósito (no genérica): en vez de tocar esos 50 consumidores, la
 *  conversión pasa una sola vez acá, justo donde `ClienteAsideMenuRender`/
 *  `RegistroFincaPaso3Render` leen el listado. `isAgricultura`/`isGanaderia`
 *  se derivan de `tipoFinca` (no viajan aparte, ver comentario del proto en
 *  login.proto); `isEliminado` siempre `false` (el server ya filtra
 *  `IsEliminado == false` al armar la lista, así que toda finca que llega
 *  acá está viva). */
export function toClienteFincaBaseResponse(
  finca: StoredFinca,
): ClienteFincaBaseResponse {
  return {
    id: finca.id,
    isEliminado: false,
    clienteId: finca.clienteId,
    nombreFinca: finca.nombreFinca,
    hectareasFinca: finca.hectareasFinca,
    hectareasProductivas: finca.hectareasProductivas,
    tipoFinca: finca.tipoFinca,
    isAgricultura: finca.tipoFinca === 0,
    isGanaderia: finca.tipoFinca === 1,
    ganaderiaCrecimientoDiarioGr: finca.ganaderiaCrecimientoDiarioGr,
    ganaderiaDiasDescansoOptimo: finca.ganaderiaDiasDescansoOptimo,
    ganaderiaDiasOcupacionOptimo: finca.ganaderiaDiasOcupacionOptimo,
    ganaderiaAnimalesXHectareaOptimo: finca.ganaderiaAnimalesXHectareaOptimo,
    arbolesMonitorear: finca.arbolesMonitorear,
    ubicacion: finca.ubicacion,
    sateliteAreaGeografica: finca.sateliteAreaGeografica,
    uriOrtomosaico: finca.uriOrtomosaico ?? null,
    conteoPreRender: finca.conteoPreRender ?? null,
    departamentoId: finca.departamentoId ?? null,
    departamentoNombre: finca.departamentoNombre ?? null,
    municipioId: finca.municipioId ?? null,
    municipioNombre: finca.municipioNombre ?? null,
    departamentoRegionId: finca.departamentoRegionId ?? null,
    departamentoRegionNombre: finca.departamentoRegionNombre ?? null,
    pasoRegistroCompletado: finca.pasoRegistroCompletado,
  };
}

/** Adapta un `FincaConfiguracionItem` (gRPC, `ConfiguracionFincaService` —
 *  guardado de árboles a monitorear/ganadería) al shape `ClienteFincaBaseResponse`
 *  (REST), mismo criterio que `toClienteFincaBaseResponse`: el mensaje trae el
 *  mismo set de campos que `Finca` (login) menos `lotes`, con las geometrías en
 *  WKB en vez de ya decodificadas. Lo usan `FincaArbolesSaveService`/
 *  `FincaGanaderiaSaveService` para poder reusar `fromClienteFincaBaseResponse`
 *  al persistir en `grpcFincaLoteDatabase` sin duplicar el mapeo campo por campo. */
export function toClienteFincaBaseResponseFromConfiguracionItem(
  item: FincaConfiguracionItem,
): ClienteFincaBaseResponse {
  return {
    id: item.id,
    isEliminado: false,
    clienteId: item.clienteId,
    nombreFinca: item.nombreFinca,
    hectareasFinca: item.hectareasFinca,
    hectareasProductivas: item.hectareasProductivas,
    tipoFinca: item.tipoFinca,
    isAgricultura: item.tipoFinca === 0,
    isGanaderia: item.tipoFinca === 1,
    ganaderiaCrecimientoDiarioGr: item.ganaderiaCrecimientoDiarioGr,
    ganaderiaDiasDescansoOptimo: item.ganaderiaDiasDescansoOptimo,
    ganaderiaDiasOcupacionOptimo: item.ganaderiaDiasOcupacionOptimo,
    ganaderiaAnimalesXHectareaOptimo: item.ganaderiaAnimalesXHectareaOptimo,
    arbolesMonitorear: item.arbolesMonitorear,
    ubicacion: item.ubicacion ? wkbToPoint(item.ubicacion) : null,
    sateliteAreaGeografica: item.sateliteAreaGeografica
      ? wkbToPolygon(item.sateliteAreaGeografica)
      : null,
    uriOrtomosaico: item.uriOrtomosaico ?? null,
    conteoPreRender: item.conteoPreRender ?? null,
    departamentoId: item.departamentoId ?? null,
    departamentoNombre: item.departamentoNombre ?? null,
    municipioId: item.municipioId ?? null,
    municipioNombre: item.municipioNombre ?? null,
    departamentoRegionId: item.departamentoRegionId ?? null,
    departamentoRegionNombre: item.departamentoRegionNombre ?? null,
    pasoRegistroCompletado: item.pasoRegistroCompletado,
  };
}

/** Camino inverso de `toClienteFincaBaseResponse`: lo usan los guardados que
 *  siguen en REST (`FincaGanaderiaSaveService`/`FincaArbolesSaveService`) para
 *  volcar la finca actualizada que ya les devuelve su propio `onSaveEnd` acá
 *  también, vía `upsertFincaAsync` — así el sidebar ve el cambio sin esperar
 *  al próximo login. `isEliminado`/`isAgricultura`/`isGanaderia`/`tipo` no
 *  viajan (no existen en `FincaParaGuardar`, ver comentario de ese tipo). */
export function fromClienteFincaBaseResponse(
  finca: ClienteFincaBaseResponse,
): FincaParaGuardar {
  return {
    id: finca.id,
    clienteId: finca.clienteId,
    nombreFinca: finca.nombreFinca,
    hectareasFinca: finca.hectareasFinca,
    hectareasProductivas: finca.hectareasProductivas,
    tipoFinca: finca.tipoFinca,
    ganaderiaCrecimientoDiarioGr: finca.ganaderiaCrecimientoDiarioGr,
    ganaderiaDiasDescansoOptimo: finca.ganaderiaDiasDescansoOptimo,
    ganaderiaDiasOcupacionOptimo: finca.ganaderiaDiasOcupacionOptimo,
    ganaderiaAnimalesXHectareaOptimo: finca.ganaderiaAnimalesXHectareaOptimo,
    arbolesMonitorear: finca.arbolesMonitorear,
    ubicacion: finca.ubicacion,
    sateliteAreaGeografica: finca.sateliteAreaGeografica,
    uriOrtomosaico: finca.uriOrtomosaico ?? undefined,
    conteoPreRender: finca.conteoPreRender ?? undefined,
    departamentoId: finca.departamentoId ?? undefined,
    departamentoNombre: finca.departamentoNombre ?? undefined,
    municipioId: finca.municipioId ?? undefined,
    municipioNombre: finca.municipioNombre ?? undefined,
    departamentoRegionId: finca.departamentoRegionId ?? undefined,
    departamentoRegionNombre: finca.departamentoRegionNombre ?? undefined,
    pasoRegistroCompletado: finca.pasoRegistroCompletado,
  };
}
